/**
 * language configuration & lazy loading
 * based on: https://kazupon.github.io/vue-i18n/guide/lazy-loading.html
 *
 */
import { createI18n } from 'vue-i18n';

import messages_ar from './ar.json';
import messages_bg from './bg.json';
import messages_cs from './cs.json';
import messages_da from './da.json';
import messages_de from './de.json';
import messages_de_AT from './de-AT.json';
import messages_de_BE from './de-BE.json';
import messages_de_CH from './de-CH.json';
import messages_el from './el.json';
import messages_en from './en.json';
import messages_en_AU from './en-AU.json';
import messages_en_CA from './en-CA.json';
import messages_en_GB from './en-GB.json';
import messages_en_NZ from './en-NZ.json';
import messages_en_ZA from './en-ZA.json';
import messages_es from './es.json';
import messages_es_MX from './es-MX.json';
import messages_et from './et.json';
import messages_fi from './fi.json';
import messages_fr from './fr.json';
import messages_fr_BE from './fr-BE.json';
import messages_fr_CA from './fr-CA.json';
import messages_fr_CH from './fr-CH.json';
import messages_fr_LU from './fr-LU.json';
import messages_he from './he.json';
import messages_hr from './hr.json';
import messages_hu from './hu.json';
import messages_id from './id.json';
import messages_it_CH from './it-CH.json';
import messages_it from './it.json';
import messages_ja from './ja.json';
import messages_kk from './kk.json';
import messages_ko from './ko.json';
import messages_lt from './lt.json';
import messages_lv from './lv.json';
import messages_mk from './mk.json';
import messages_nb from './nb.json';
import messages_nl_BE from './nl-BE.json';
import messages_nl from './nl.json';
import messages_pl from './pl.json';
import messages_pt from './pt.json';
import messages_pt_BR from './pt-BR.json';
import messages_ro from './ro.json';
import messages_ru from './ru.json';
import messages_sk from './sk.json';
import messages_sl from './sl.json';
import messages_sq from './sq.json';
import messages_sr from './sr.json';
import messages_sv from './sv.json';
import messages_th from './th.json';
import messages_tr from './tr.json';
import messages_uk from './uk.json';
import messages_vi from './vi.json';
import messages_zh from './zh.json';

import axios from 'axios';

const messages = {
  ar: messages_ar,
  bg: messages_bg,
  cs: messages_cs,
  da: messages_da,
  'de-AT': messages_de_AT,
  'de-BE': messages_de_BE,
  'de-CH': messages_de_CH,
  de: messages_de,
  el: messages_el,
  'en-AU': messages_en_AU,
  'en-CA': messages_en_CA,
  'en-GB': messages_en_GB,
  'en-NZ': messages_en_NZ,
  'en-ZA': messages_en_ZA,
  en: messages_en,
  'es-MX': messages_es_MX,
  es: messages_es,
  et: messages_et,
  fi: messages_fi,
  'fr-BE': messages_fr_BE,
  'fr-CA': messages_fr_CA,
  'fr-CH': messages_fr_CH,
  'fr-LU': messages_fr_LU,
  fr: messages_fr,
  he: messages_he,
  hr: messages_hr,
  hu: messages_hu,
  id: messages_id,
  'it-CH': messages_it_CH,
  it: messages_it,
  ja: messages_ja,
  kk: messages_kk,
  ko: messages_ko,
  lt: messages_lt,
  lv: messages_lv,
  mk: messages_mk,
  nb: messages_nb,
  'nl-BE': messages_nl_BE,
  nl: messages_nl,
  pl: messages_pl,
  'pt-BR': messages_pt_BR,
  pt: messages_pt,
  ro: messages_ro,
  ru: messages_ru,
  sk: messages_sk,
  sl: messages_sl,
  sq: messages_sq,
  sr: messages_sr,
  sv: messages_sv,
  th: messages_th,
  tr: messages_tr,
  uk: messages_uk,
  vi: messages_vi,
  zh: messages_zh,
};

type SupportedLocale = keyof typeof messages;
const supportedLocales = Object.keys(messages) as SupportedLocale[];
supportedLocales.sort((a, b) => b == a ? 0 : a < b ? 1 : -1); // sort desc
console.debug('supportedLocales (desc):', supportedLocales);

// not very useful because immediately redirected to OIDCP - look at OpenIdConnectCallback
const getInitialLocale = () => {
  let loc = new URLSearchParams(window.location.search).get('locale');
  loc = toSupportedLocale(loc, 'en');
  console.debug(`init-locale:${loc}`);
  return loc;
};

export const toSupportedLocale = (loc: string | null, def: SupportedLocale) => {
  if (!loc) return def;

  const index = supportedLocales.findIndex(locale => {
    return loc.toLowerCase()
      .startsWith(locale.toLowerCase()); // search if it starts with a supported locale
  });
  return index < 0 ? def : supportedLocales[index];
};

export const localeDir = (locale: string): 'ltr' | 'rtl' => {
  if (locale === 'ar' || locale === 'he') return 'rtl';
  return 'ltr';
};

export const changeLocale = (locale: string | null) => {
  if (locale) {
    const lang = toSupportedLocale(locale, 'en');
    console.log(`[$changeLocale] requested:${locale} supported:${lang}`);
    i18n.global.locale = lang;
    axios.defaults.headers.common['Accept-Language'] = lang;
    document.querySelector('html')?.setAttribute('lang', lang);
    document.querySelector('html')?.setAttribute('dir', localeDir(lang));
  }
};

const getLocale = async () => (
  axios.get(`${import.meta.env.VITE_DSS_SVC}/lang`, { responseType: 'text' })
);

/**
 * set the locale based on the 'locale' query parameter of on server locale
 */
export const initLocale = async (loc: string | null) => {
  if (loc) {
    console.debug(`[initLocale] param-locale: ${loc}`);
    changeLocale(loc);

  } else {
    // get locale from server. improve by sending the locale in #url-fragment ?
    await getLocale()
      .then(resp => {
        console.debug(`[initLocale] browser-locale: ${resp.data}`);
        changeLocale(resp.data);
      })
      .catch((e) => {
        console.info('[initLocale] no locale from server', e);
      });
  }
};

export const languageCodes = Object.keys(messages_en.dss.lang);

export const i18n = createI18n({
  locale: getInitialLocale(), // set locale
  fallbackLocale: 'en',
  messages
});

export const localizedLink = (links: Record<string, string>) => {
  return links[i18n.global.locale] ?? links.en;
};
